import React, { useCallback, useMemo } from 'react';
import { Box, Flex, SpacingStack, TextV2 } from '@withjoy/joykit';
import { StyledDivider } from '../../../RegistryDialog/DialogFooter.styles';
import { InSummaryItem } from '../../../ShoppingCart/components/SummaryItemList/InSummaryItem';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import type { MonetaryValue } from '../../ExpressCheckout.provider';
import { useExpressCheckoutContext } from '../../ExpressCheckout.provider';
import { useCurrencyContext } from '@shared/utils/currency';
import { useRegistryShippingAndHandlingLineLabel } from '@apps/registry/common/hooks/useRegistryShippingAndHandlingLineLabel';

export const PriceLine = ({ label, price, bold }: { label: string; price: string | number; bold?: boolean }) => (
  <Flex justifyContent={'space-between'}>
    <TextV2
      typographyVariant={bold ? 'hed3' : 'label2'}
      color={bold ? 'mono14' : 'mono12'}
    >
      {label}
    </TextV2>
    <TextV2
      typographyVariant={bold ? 'hed3' : 'label2'}
      color={bold ? 'mono14' : 'mono12'}
    >
      {price}
    </TextV2>
  </Flex>
);

export const PaymentSummary = () => {
  const { t } = useTranslation('sharedRegistry');
  const { subtotal, total, salesTax, freeDeliveryPriceText, pendingSalesTaxPriceText, eCard } = t('shoppingCart');
  const currencyFormatter = useCurrencyContext();
  const { getDeliveryLineLabel, getSurchargeLineLabel } = useRegistryShippingAndHandlingLineLabel();
  const {
    state: { purchaseProducts, costInfo, giftWrap, totalPrice, productsPrice, eCardPrice, currencyCode, applicableTaxesTotal },
  } = useExpressCheckoutContext();

  const formatPrice = useCallback(
    (value: MonetaryValue) => currencyFormatter.formatCurrency(value.valueInMinorUnits / 100, { form: 'short', currency: currencyCode }),
    [currencyCode, currencyFormatter],
  );

  const formatPriceOrPending = (value?: MonetaryValue | null) => {
    return value ? formatPrice(value) : pendingSalesTaxPriceText();
  };

  const deliveryPrice = !costInfo ? pendingSalesTaxPriceText() : costInfo.isFreeShipping ? freeDeliveryPriceText() : formatPrice(costInfo.deliveryTotalValue);

  const deliveryPriceLine = (
    <PriceLine
      key="costLine-delivery"
      label={getDeliveryLineLabel()}
      price={deliveryPrice}
    />
  );

  const surchargePriceLine = !costInfo ? (
    <PriceLine
      key="costLine-surcharge"
      label={getSurchargeLineLabel()}
      price={pendingSalesTaxPriceText()}
    />
  ) : costInfo.bulkSurchargeTotalValue.valueInMinorUnits > 0 ? (
    <PriceLine
      key="costLine-surcharge"
      label={getSurchargeLineLabel(costInfo.bulkSurchargeShippingLines.length)}
      price={formatPrice(costInfo.bulkSurchargeTotalValue)}
    />
  ) : null;

  const additionalCostPriceLines = useMemo(
    () =>
      (costInfo?.additionalCostLines ?? []).map((line, index) => (
        <PriceLine
          key={`costLine-${line.title}-${index}`}
          label={line.title}
          price={formatPrice(line.lineValue)}
        />
      )),
    [costInfo?.additionalCostLines, formatPrice],
  );

  return (
    <>
      <Box
        marginTop={pxToRem(-24)}
        marginRight={pxToRem(-16)}
      >
        {purchaseProducts.map(product => (
          <InSummaryItem
            key={product.registryItemId}
            inCartProduct={{ product, quantity: product.quantity, registryItemId: product.registryItemId }}
          />
        ))}
      </Box>
      <StyledDivider />
      <SpacingStack spacing={pxToRem(8)}>
        <PriceLine
          label={subtotal()}
          price={formatPrice(productsPrice)}
        />
        {deliveryPriceLine}
        {surchargePriceLine}
        {additionalCostPriceLines}
        {giftWrap?.wrapOption === 'eCard' && (
          <PriceLine
            label={eCard()}
            price={formatPriceOrPending(eCardPrice)}
          />
        )}
        <PriceLine
          label={salesTax()}
          price={formatPriceOrPending(applicableTaxesTotal)}
        />
      </SpacingStack>
      <StyledDivider />
      <PriceLine
        label={total()}
        price={formatPriceOrPending(totalPrice)}
        bold
      />
    </>
  );
};
